import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteModal } from "@/components/QuoteModal";
import { Ship, Plane, Truck, Box, FileCheck, Shield, LineChart, CheckCircle, Globe, Clock, TrendingUp, Award, Package, Users, Zap, Building, MapPin, BarChart3 } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import bannerTerminal from "@/assets/banner-terminal.jpg";
import airFreight from "@/assets/air-freight.jpg";
import warehouse from "@/assets/warehouse.jpg";
import projectCargo from "@/assets/project-cargo.jpg";

const Services = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const services = [
    {
      icon: Ship,
      title: "Ocean Freight",
      description: "FCL and LCL services with door-to-door delivery, consolidated shipping, and charter options. Complete ISF filing and documentation support.",
      features: ["Full Container Load (FCL)", "Less than Container Load (LCL)", "Door-to-Door Service", "Consolidated Shipping"],
    },
    {
      icon: Plane,
      title: "Air Freight",
      description: "Fast and reliable air cargo solutions for time-sensitive shipments. Direct flights and consolidated options available worldwide.",
      features: ["Express Delivery", "Standard Air Cargo", "Dangerous Goods Handling", "Charter Services"],
    },
    {
      icon: Truck,
      title: "Intermodal & Road Transport",
      description: "Seamless land transport solutions connecting ports to final destinations. Full tracking and scheduled deliveries.",
      features: ["Port to Door", "Cross-border Transport", "Last-mile Delivery", "Temperature Controlled"],
    },
    {
      icon: Box,
      title: "Project & Heavy Cargo",
      description: "Specialized handling for oversized and heavy equipment. Custom engineering solutions and route surveys.",
      features: ["Heavy Machinery", "Wind Turbines", "Industrial Equipment", "Route Planning"],
    },
    {
      icon: FileCheck,
      title: "Customs Brokerage",
      description: "Expert customs clearance and compliance support. Electronic submission, HS classification, and duties optimization.",
      features: ["Electronic Filing", "HS Classification", "Duty Optimization", "Audit Support"],
    },
    {
      icon: Shield,
      title: "Insurance & Claims",
      description: "Comprehensive cargo insurance and claims management. Protect your shipments with our tailored insurance solutions.",
      features: ["All-risk Coverage", "Marine Insurance", "Claims Assistance", "Risk Assessment"],
    },
    {
      icon: LineChart,
      title: "Supply Chain Consulting",
      description: "Strategic logistics planning and optimization. Reduce costs and improve efficiency with our expert guidance.",
      features: ["Cost Analysis", "Route Optimization", "Vendor Management", "Process Improvement"],
    },
  ];

  const capabilities = [
    { icon: Globe, value: "120+", label: "Partner Ports Worldwide", desc: "Connected to major trade lanes globally" },
    { icon: Ship, value: "50,000+", label: "Annual Shipments", desc: "Containers moved every year" },
    { icon: Users, value: "2,500+", label: "Active Clients", desc: "Businesses trust our services" },
    { icon: Clock, value: "99.8%", label: "On-Time Delivery", desc: "Industry-leading reliability" },
  ];

  const whyChooseUs = [
    { icon: Shield, title: "Global Network Advantage", desc: "Our California headquarters and worldwide partners provide 24/7 global coverage and regional expertise across Europe, Mediterranean, Asia-Pacific, and Americas." },
    { icon: TrendingUp, title: "Cost Optimization", desc: "Our consolidated volumes and strategic partnerships deliver 15-25% savings on freight rates without compromising service quality." },
    { icon: Zap, title: "Digital Innovation", desc: "Real-time tracking, automated documentation, and AI-powered route optimization keep you informed and efficient." },
    { icon: Award, title: "40+ Years Experience", desc: "Industry-leading maritime operations combined with modern logistics expertise. ISO certified, AEO authorized, and industry recognized." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive end-to-end logistics solutions designed to meet the unique needs of your business. From our corporate headquarters in California, USA, we deliver excellence at every step.
            </p>
          </div>

          {/* Banner Section */}
          <div className="relative rounded-xl overflow-hidden shadow-2xl mb-16 animate-fade-in">
            <img src={bannerTerminal} alt="Port Terminal" className="w-full h-[300px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
              <div className="container mx-auto px-8 text-primary-foreground">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Trusted by Industry Leaders</h2>
                <p className="text-lg max-w-2xl">
                  Operating from California with connections to 120+ ports worldwide
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-all border-2 hover:border-primary hover-scale animate-fade-in">
                  <CardContent className="p-8">
                    <Icon className="h-12 w-12 text-primary mb-4" />
                    <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                    <p className="text-muted-foreground mb-6">{service.description}</p>
                    <div className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary mr-2" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full hover-scale" asChild>
                      <NavLink to={
                        service.title === "Ocean Freight" ? "/ocean-freight" :
                          service.title === "Air Freight" ? "/air-freight" :
                            service.title === "Intermodal & Road Transport" ? "/intermodal" :
                              service.title === "Project & Heavy Cargo" ? "/project-cargo" :
                                service.title === "Customs Brokerage" ? "/customs-brokerage" :
                                  service.title === "Insurance & Claims" ? "/insurance" :
                                    service.title === "Supply Chain Consulting" ? "/supply-chain" :
                                      "/services"
                      }>
                        Learn More
                      </NavLink>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Our Capabilities Section */}
          <section className="py-16 bg-gradient-to-r from-maritime-dark to-maritime-light rounded-xl text-primary-foreground mb-16 animate-fade-in">
            <div className="container mx-auto px-8">
              <h2 className="text-3xl font-bold text-center mb-12">Our Capabilities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {capabilities.map((cap, idx) => {
                  const Icon = cap.icon;
                  return (
                    <div key={idx} className="text-center">
                      <Icon className="h-12 w-12 mx-auto mb-4 text-accent" />
                      <div className="text-4xl font-bold mb-2">{cap.value}</div>
                      <p className="font-semibold mb-1">{cap.label}</p>
                      <p className="text-sm opacity-80">{cap.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Why Choose Costal Meridian */}
          <section className="mb-16 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose Costal Meridian?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {whyChooseUs.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <Card key={idx} className="hover:shadow-lg transition-all hover-scale">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon className="h-7 w-7 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                          <p className="text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Service Showcases */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Featured Solutions</h2>

            {/* Air Freight Showcase */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl mb-8 animate-fade-in">
              <img src={airFreight} alt="Air Freight Services" className="w-full h-[350px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
                <div className="container mx-auto px-8 text-primary-foreground">
                  <Plane className="h-12 w-12 mb-4 text-accent" />
                  <h3 className="text-3xl font-bold mb-3">Express Air Freight</h3>
                  <p className="text-lg mb-4 max-w-xl">
                    Time-critical global shipments to anywhere in the world within 24-72 hours. Charter options available.
                  </p>
                  <Button variant="secondary" asChild className="hover-scale">
                    <NavLink to="/air-freight">Explore Air Freight</NavLink>
                  </Button>
                </div>
              </div>
            </div>

            {/* Warehousing Showcase */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl mb-8 animate-fade-in">
              <img src={warehouse} alt="Warehousing Services" className="w-full h-[350px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
                <div className="container mx-auto px-8 text-primary-foreground">
                  <Package className="h-12 w-12 mb-4 text-accent" />
                  <h3 className="text-3xl font-bold mb-3">Strategic Warehousing</h3>
                  <p className="text-lg mb-4 max-w-xl">
                    50,000+ sqm of warehouse space in our strategic facility network. Bonded facilities, temperature control, and WMS integration.
                  </p>
                  <Button variant="secondary" asChild className="hover-scale">
                    <NavLink to="/warehousing">Explore Warehousing</NavLink>
                  </Button>
                </div>
              </div>
            </div>

            {/* Project Cargo Showcase */}
            <div className="relative rounded-xl overflow-hidden shadow-2xl animate-fade-in">
              <img src={projectCargo} alt="Project Cargo Services" className="w-full h-[350px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
                <div className="container mx-auto px-8 text-primary-foreground">
                  <Box className="h-12 w-12 mb-4 text-accent" />
                  <h3 className="text-3xl font-bold mb-3">Project & Heavy Cargo</h3>
                  <p className="text-lg mb-4 max-w-xl">
                    Heavy lift expertise for oversized cargo. Engineering surveys, route planning, and multimodal coordination.
                  </p>
                  <Button variant="secondary" asChild className="hover-scale">
                    <NavLink to="/project-cargo">Explore Project Cargo</NavLink>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="bg-secondary rounded-lg p-12 mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {["Quote", "Booking", "Documentation", "Tracking", "Delivery"].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-lg">{step}</h3>
                </div>
              ))}
            </div>
          </section>

          {/* Industry Solutions */}
          <section className="bg-secondary rounded-lg p-12 mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Industry-Specific Solutions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { industry: "Retail & E-commerce", desc: "Fast fulfillment, seasonal peaks, returns handling", icon: BarChart3 },
                { industry: "Automotive", desc: "Just-in-time delivery, parts logistics, project cargo", icon: Truck },
                { industry: "Energy & Heavy Machinery", desc: "Oversized cargo, specialized equipment, route surveys", icon: Building },
                { industry: "Food & Pharma", desc: "Temperature-controlled, regulatory compliance, cold chain", icon: Shield },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <Card key={idx} className="text-center hover:shadow-lg transition-all hover-scale">
                    <CardContent className="p-6">
                      <Icon className="h-10 w-10 text-primary mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-3">{item.industry}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Technology */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Technology & Innovation</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Ship className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Real-Time Tracking</h3>
                  <p className="text-muted-foreground">
                    GPS tracking, milestone updates, ETA notifications, and API integration for all shipments.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileCheck className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Digital Documentation</h3>
                  <p className="text-muted-foreground">
                    Electronic bill of lading, digital signatures, automated customs filing, paperless workflow.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Data Security</h3>
                  <p className="text-muted-foreground">
                    GDPR compliant, encrypted transmissions, secure client portal, audit trail logging.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Global Locations */}
          <section className="mb-16 bg-secondary rounded-lg p-12 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Our Corporate Headquarters</h2>
            <div className="max-w-xl mx-auto">
              <Card className="hover-scale">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">USA Corporate Headquarters</h3>
                      <p className="text-muted-foreground">Costa Mesa, California</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-primary mr-2" />Global administration and routing</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-primary mr-2" />Transpacific and global logistics engineering</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-primary mr-2" />24/7 control tower operations</li>
                    <li className="flex items-center"><CheckCircle className="h-4 w-4 text-primary mr-2" />US customs and global compliance</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center bg-primary text-primary-foreground rounded-lg p-12">
            <h2 className="text-3xl font-bold mb-4">Need a Custom Solution?</h2>
            <p className="text-xl mb-8 opacity-90">
              Our team is ready to create a tailored logistics plan for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Get Your Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Our Team</NavLink>
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default Services;